import * as React from "react";
import { IClinician, IExperienceLevelOption } from "intake_shared/src/types";
import { getCredentialExpirationNotices } from "intake_shared/src/util";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { canViewCredentialExpirationStatus } from "../../../../appSettings";
import { AclContext } from "../../../../contexts/AclContext";
import { Badge } from "../../bits/Badge/Badge";
import styles from "./CredentialExpirationStatus.module.scss";

interface CredentialExpirationStatusProps {
    clinician: IClinician;
    experienceLevelOptions: IExperienceLevelOption[];
}

function formatExpirationDate(value: string): string {
    const [year, month, day] = value.split("-").map(Number);
    return new Intl.DateTimeFormat("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
    }).format(new Date(year, month - 1, day));
}

export default function CredentialExpirationStatus({
    clinician,
    experienceLevelOptions,
}: CredentialExpirationStatusProps) {
    const aclContext = React.useContext(AclContext);
    const canView = canViewCredentialExpirationStatus(
        aclContext?.authUser.user?.email,
        Boolean(aclContext?.authUser.acl?.intake || aclContext?.userAcl.acl?.intake)
    );
    const notices = getCredentialExpirationNotices(
        clinician.experience_levels ?? {},
        experienceLevelOptions
    );

    if (!canView || notices.length < 1) {
        return null;
    }

    const expiredNotices = notices.filter((notice) => notice.status === "expired");
    const expiringSoonNotices = notices.filter(
        (notice) => notice.status === "expiring_soon"
    );
    const hasExpired = expiredNotices.length > 0;
    const tooltipId = `credential-expiration-${
        clinician.id || clinician.email
    }`.replace(/[^a-zA-Z0-9-_]/g, "-");

    return (
        <OverlayTrigger
            placement="bottom"
            trigger={["hover", "focus"]}
            overlay={
                <Tooltip id={tooltipId} className={styles.tooltip}>
                    {expiredNotices.length > 0 && (
                        <div>
                            <strong>Expired</strong>
                            {expiredNotices.map((notice) => (
                                <div key={notice.experienceLevel}>
                                    {notice.experienceLevel}{" "}
                                    {notice.type.toLowerCase()}
                                    {" — expired "}
                                    {formatExpirationDate(notice.expirationDate)}
                                </div>
                            ))}
                        </div>
                    )}
                    {expiringSoonNotices.length > 0 && (
                        <div>
                            <strong>Expiring soon</strong>
                            {expiringSoonNotices.map((notice) => (
                                <div key={notice.experienceLevel}>
                                    {notice.experienceLevel}{" "}
                                    {notice.type.toLowerCase()}
                                    {" — expires "}
                                    {formatExpirationDate(notice.expirationDate)}
                                </div>
                            ))}
                        </div>
                    )}
                </Tooltip>
            }
        >
            <button
                type="button"
                className={styles.trigger}
                onClick={(event) => event.stopPropagation()}
                aria-label={`${notices.length} credential expiration alert${
                    notices.length === 1 ? "" : "s"
                }`}
            >
                <Badge
                    content={
                        hasExpired
                            ? `Expired credential${
                                  expiredNotices.length === 1 ? "" : "s"
                              }`
                            : `Credential${
                                  expiringSoonNotices.length === 1 ? "" : "s"
                              } expiring soon`
                    }
                    textColor={hasExpired ? "#711920" : "#674C00"}
                    bgColor={hasExpired ? "#FEC1C6" : "#FFE7A3"}
                />
            </button>
        </OverlayTrigger>
    );
}
